import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { AdsQueryVariables } from './useAdsAnalyticsParams';
import { getDefaultSelectedCampaignsAndSets } from '../components/ad-campaigns-and-sets-select';

interface AdAccount {
  id: string;
  name: string;
}

interface AdCampaign {
  id: string;
  name: string;
  adSets?: AdSet[];
  hasPaidPartnershipAds?: boolean;
}

interface AdSet {
  id: string;
  name: string;
  hasPaidPartnershipAds?: boolean;
}

interface Member {
  id: string;
  name?: string;
  email?: string;
  profilePicture?: string;
}

interface UseAdsDefaultValuesProps {
  queryVariables: AdsQueryVariables;
  adAccounts: AdAccount[];
  adCampaigns: AdCampaign[];
  members?: Member[]; // Optional for now, will be used when backend supports member filtering
}

// Removed shouldUseDefaultsForFilter - no longer needed with simplified logic

/**
 * Helper function to identify members that should be auto-selected.
 * By default, it should auto-select any member that has paid partnership ads
 * or a campaign/set with UGC, Influencer, or Aspire name.
 *
 * Note: This is a placeholder implementation until backend supports member-campaign associations.
 * Currently returns empty array but can be enhanced when we have the data.
 */
const getDefaultSelectedMembers = (_members: Member[] = []): string[] => {
  // TODO: Implement once backend provides member-campaign association data
  // For now, return empty array since we don't have member-campaign associations

  // Future implementation will:
  // 1. Find campaigns/ad sets with paid partnership ads (hasPaidPartnershipAds: true)
  // 2. Find campaigns/ad sets with UGC/Influencer/Aspire in names
  // 3. Return member IDs associated with those campaigns/ad sets

  return []; // Return empty for now
};

/**
 * Hook to determine default filter values for the Ads Dashboard.
 *
 * IMPORTANT: This hook implements a simple rule:
 * - If URL has ANY query parameters → return query variables as-is (no defaults)
 * - If URL has NO query parameters → apply all business logic defaults
 *
 * This ensures consistent behavior with useUrlInitialization and prevents
 * circular dependencies between the two hooks.
 */
export const useAdsDefaultValues = ({
  queryVariables,
  adAccounts,
  adCampaigns,
  members: _members = [],
}: UseAdsDefaultValuesProps) => {
  const location = useLocation();

  return useMemo(() => {
    // CRITICAL: Check if ANY query params exist at all
    // If any params exist, don't apply defaults (consistent with useUrlInitialization)
    const hasAnyQueryParams = location.search.length > 0;

    // If URL has any params, just return query variables as-is (no defaults)
    if (hasAnyQueryParams) {
      return {
        adAccountIds: queryVariables?.adAccountIds || [],
        adCampaignAndSetIds: queryVariables?.adCampaignAndSetIds || [],
        adObjectiveIds: queryVariables?.adObjectiveIds || [],
        memberIds: queryVariables?.memberIds || [],
        compareWithPartnershipAds: queryVariables?.compareWithPartnershipAds || false,
        showNonAttributedData: queryVariables?.showNonAttributedData || false,
      };
    }

    // Only reach here if URL has NO query params at all (/ads)
    // Apply all defaults according to the specified logic:

    // 1. By default, all active ad accounts for insights should be included
    const defaultAdAccountIds = adAccounts.map((account) => account.id);

    // 2. By default, auto-select any campaign/set with paid partnership ads or UGC/Influencer/Aspire names
    const defaultAdCampaignAndSetIds = getDefaultSelectedCampaignsAndSets(adCampaigns);

    // 3. By default, auto-select any member with paid partnership ads or campaign/set with UGC/Influencer/Aspire names
    const defaultMemberIds = getDefaultSelectedMembers(_members);

    return {
      adAccountIds: defaultAdAccountIds,
      adCampaignAndSetIds: defaultAdCampaignAndSetIds,
      adObjectiveIds: [],
      memberIds: defaultMemberIds,
      compareWithPartnershipAds: false,
      showNonAttributedData: false,
    };
  }, [queryVariables, adAccounts, adCampaigns, _members, location.search]);
};
